import { useEffect, useState } from "react"
import {
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Box,
  Button,
  Divider,
  Collapse,
  Grid,
  Paper,
  Badge,
  Tooltip,
  Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import styled from "@emotion/styled"
import { RestartAlt } from "@mui/icons-material"
import { getReportClients, getReportUsers } from "../redux/ducks/reports"
import { getDepartments } from "../redux/ducks/department"
import { useDispatch } from "react-redux"
import AppDatePicker from "./AppDatePicker"
import TagPicker from "./TagPicker"

export const DEFAULT_FILTERS = {
    client: "Clients",
    group: "",
    project: "",
    task: "",
    department: "",
    responsible: "",
    assignee: [],
    creationDateStart: "",
    creationDateEnd: "",
    dueDateStart: "",
    dueDateEnd: "",
    startDateStart: "",
    startDateEnd: "",
    lastActivityDateStart: "",
    lastActivityDateEnd: "",
    status: "Active",
    workStatus: [],
    tags: [],
    priority: "",
    starred: "",
};

const WORK_STATUSES = ["Awaiting Authorization", "Pending", "In Progress", "Testing", "Ready for Review", "Approved", "Completed", "On Hold", "Cancelled"];

const ADVANCED_KEYS = Object.keys(DEFAULT_FILTERS).filter((key) => key !== "client" && key !== "status");

const countAdvanced = (filters) =>
    ADVANCED_KEYS.filter((key) => {
        const value = filters[key];
        if (Array.isArray(value)) return value.length > 0;
        return value !== "" && value !== null && value !== undefined;
    }).length;

const FiltersPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const PrimaryRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
}));

const PrimaryField = styled(FormControl)(({ theme }) => ({
    minWidth: '200px',
    flex: '1 1 200px',
    [theme.breakpoints.up('md')]: {
        flex: '0 1 240px',
    },
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
}));

const ExpandIcon = styled(ExpandMoreIcon, { shouldForwardProp: (prop) => prop !== 'expanded' })(({ theme, expanded }) => ({
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: theme.transitions.create('transform'),
}));

const AdvancedGrid = styled(Grid)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const RangeLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const RangeRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

const RangeField = styled(Box)({
    width: '50%',
});

const MenuItemDivider = styled(Divider)(({ theme }) => ({
    width: '100%',
    margin: theme.spacing(1),
}));

const DateRange = ({ label, from, to, onFromChange, onToChange }) => (
    <Box>
        <RangeLabel variant="caption">{label}</RangeLabel>
        <RangeRow>
            <RangeField>
                <AppDatePicker size="small" label="From" value={from || ""} maxDate={to || undefined} onChange={onFromChange} />
            </RangeField>
            <RangeField>
                <AppDatePicker size="small" label="To" value={to || ""} minDate={from || undefined} onChange={onToChange} />
            </RangeField>
        </RangeRow>
    </Box>
);

const MasterReportFilters = ({ values, onValuesChange, onSearch, onReset }) => {
    const dispatch = useDispatch();
    const [clients, setClients] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [expanded, setExpanded] = useState(false);

    const setValue = (name, value) => onValuesChange({ ...values, [name]: value });

    const handleChange = (event) => {
        const { name, value } = event.target;
        setValue(name, value);
    };

    const advancedCount = countAdvanced(values);

    const handleSubmit = (event) => {
        event.preventDefault();
        onSearch();
    };

    useEffect(() => {
        dispatch(getReportClients((clients) => setClients(clients || [])));
        dispatch(getDepartments(0, 100, (departments) => setDepartments(departments || [])));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getReportUsers((users) => setUsers(users || [])));
    }, [dispatch]);

    return (
        <FiltersPaper elevation={2} component="form" onSubmit={handleSubmit}>
            <PrimaryRow>
                <PrimaryField size="small">
                    <InputLabel>Client</InputLabel>
                    <Select label="Client" name="client" value={values.client} onChange={handleChange}>
                        <MenuItem value="Clients">All Clients</MenuItem>
                        <MenuItem value="Internal">Internal</MenuItem>
                        <MenuItem value="">All</MenuItem>
                        <MenuItemDivider />
                        {clients.map((client) => (
                            <MenuItem key={client._id} value={client._id}>
                                {client.name}
                            </MenuItem>
                        ))}
                    </Select>
                </PrimaryField>
                <PrimaryField size="small">
                    <InputLabel>Show</InputLabel>
                    <Select label="Show" name="status" value={values.status} onChange={handleChange}>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                        <MenuItem value="Both">Active &amp; Completed</MenuItem>
                    </Select>
                </PrimaryField>
                <ButtonGroup>
                    <Button type="submit" variant="contained" color="primary" startIcon={<SearchIcon />}>
                        Search
                    </Button>
                    <Tooltip title="Clear every filter back to its default and run the search again">
                        <Button type="button" variant="outlined" color="secondary" startIcon={<RestartAlt />} onClick={onReset}>
                            Reset
                        </Button>
                    </Tooltip>
                    <Badge badgeContent={advancedCount} color="primary">
                        <Button type="button" variant="text" endIcon={<ExpandIcon expanded={expanded} />} onClick={() => setExpanded((prev) => !prev)}>
                            {expanded ? "Fewer filters" : "More filters"}
                        </Button>
                    </Badge>
                </ButtonGroup>
            </PrimaryRow>
            <Collapse in={expanded} unmountOnExit>
                <AdvancedGrid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField size="small" fullWidth label="Group" name="group" value={values.group} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField size="small" fullWidth label="Project" name="project" value={values.project} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField size="small" fullWidth label="Task" name="task" value={values.task} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Department</InputLabel>
                            <Select label="Department" name="department" value={values.department} onChange={handleChange}>
                                <MenuItem value="">All</MenuItem>
                                {departments.map((department) => (
                                    <MenuItem key={department._id} value={department._id}>
                                        {department.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Responsible User</InputLabel>
                            <Select label="Responsible User" name="responsible" value={values.responsible} onChange={handleChange}>
                                <MenuItem value="">Any</MenuItem>
                                <MenuItemDivider />
                                {users.map((user) => (
                                    <MenuItem key={user._id} value={user._id}>
                                        {user.fullName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Assignee</InputLabel>
                            <Select
                                label="Assignee"
                                name="assignee"
                                value={values.assignee}
                                onChange={handleChange}
                                renderValue={(selected) => users.filter((user) => selected.includes(user._id)).map((user) => user.fullName).join(", ")}
                                multiple
                            >
                                {users.map((user) => (
                                    <MenuItem key={user._id} value={user._id}>
                                        <Checkbox checked={values.assignee.includes(user._id)} />
                                        {user.fullName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select label="Status" name="workStatus" value={values.workStatus || []} onChange={handleChange} renderValue={(selected) => selected.join(", ")} multiple>
                                {WORK_STATUSES.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        <Checkbox checked={(values.workStatus || []).includes(status)} />
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TagPicker value={values.tags} onChange={(entries) => setValue("tags", entries)} source="all" allowCreate={false} placeholder="Any tag" size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select label="Priority" name="priority" value={values.priority} onChange={handleChange}>
                                <MenuItem value="">Any</MenuItem>
                                <MenuItem value="Highest">Highest</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="Low">Low</MenuItem>
                                <MenuItem value="Lowest">Lowest</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Starred</InputLabel>
                            <Select label="Starred" name="starred" value={values.starred} onChange={handleChange}>
                                <MenuItem value="">Any</MenuItem>
                                <MenuItem value="starred">Starred</MenuItem>
                                <MenuItem value="not-starred">Not Starred</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateRange
                            label="Creation Date"
                            from={values.creationDateStart}
                            to={values.creationDateEnd}
                            onFromChange={(v) => setValue("creationDateStart", v)}
                            onToChange={(v) => setValue("creationDateEnd", v)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateRange
                            label="Due Date"
                            from={values.dueDateStart}
                            to={values.dueDateEnd}
                            onFromChange={(v) => setValue("dueDateStart", v)}
                            onToChange={(v) => setValue("dueDateEnd", v)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateRange
                            label="Start Date"
                            from={values.startDateStart}
                            to={values.startDateEnd}
                            onFromChange={(v) => setValue("startDateStart", v)}
                            onToChange={(v) => setValue("startDateEnd", v)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateRange
                            label="Last Activity Date"
                            from={values.lastActivityDateStart}
                            to={values.lastActivityDateEnd}
                            onFromChange={(v) => setValue("lastActivityDateStart", v)}
                            onToChange={(v) => setValue("lastActivityDateEnd", v)}
                        />
                    </Grid>
                </AdvancedGrid>
            </Collapse>
        </FiltersPaper>
    );
}

export default MasterReportFilters;
